import React from 'react';
import {Box, capitalize, Chip, SxProps} from '@mui/material';
import moment from 'moment';
import {newUTCDate} from '../../utils/newUTCDate';
import {gridCellDef} from '../../utils/GridCellDef';
import {SaltusAny} from '../../utils/SaltusAny';

export const RenderColor: (
  colorScheme: Record<string, string>,
  keepCase?: boolean
) => gridCellDef<any> = (colorScheme, keepCase) => (params) => {
  const val = params.value?.toString();
  const isInColorScheme = val && val in colorScheme;
  const str = keepCase
    ? params.value?.toString()
    : capitalize((params.value?.toString() || '').toLocaleLowerCase());
  if (!str) {
    return null;
  }
  return (
    <Chip
      label={str.replaceAll('_', ' ')}
      sx={{
        background: isInColorScheme
          ? colorScheme[params.value?.toString() as any]
          : colorScheme.default,
      }}
    />
  );
};

export const RenderPropositionType: (
  colorScheme: Record<string, string>,
  labelMapping: Record<string, string>
) => gridCellDef<any> = (colorScheme, labelMapping) => (params) => {
  const rawValue = params.value?.toString();
  if (!rawValue) {
    return null;
  }
  const normalizedKey = rawValue.toLowerCase();
  const displayLabel = labelMapping[normalizedKey] || rawValue;
  const backgroundColor = colorScheme[normalizedKey] || colorScheme.default;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Chip
        label={displayLabel}
        sx={{
          background: backgroundColor,
        }}
      />
    </Box>
  );
};

/**
 * If `value` needs to be overridden, pass in an optional `colorKey` to correctly
 * map the Chip's background colour to the original value name
 * @param param0
 * @returns
 */
export const ColoredChip: React.FC<{
  colorScheme: Record<string, string>;
  value: string;
  colorKey?: string;
  keepCase?: boolean;
  prefix?: string;
  size?: 'small' | 'medium';
  sx?: SxProps;
}> = ({colorScheme, value, keepCase, prefix, size = 'medium', sx, colorKey}) => {
  const val = `${colorKey ?? value}`;
  const isInColorScheme = val && val in colorScheme;
  const str = keepCase
    ? value?.toString()
    : capitalize((value?.toString() || '').toLocaleLowerCase());
  if (!str) {
    return null;
  }
  return (
    <Chip
      label={`${prefix || ''}${str.replaceAll('_', ' ')}`}
      size={size}
      sx={{
        background: isInColorScheme ? colorScheme[val] : colorScheme.default,
        ...sx,
      }}
    />
  );
};

export const RenderColorBasic: (
  colorScheme: Record<string, string>,
  value: SaltusAny,
  children?: SaltusAny,
  childrenPost?: React.ReactNode,
  fontSize?: number,
  title?: string,
  noFlex?: boolean
) => React.ReactNode = (
  colorScheme,
  value,
  children,
  childrenPost,
  fontSize,
  title,
  noFlex = false
) => {
  const val = value?.toString();
  const isInColorScheme = val && val in colorScheme;
  if (!val) {
    return null;
  }
  return (
    <Box
      sx={{
        paddingTop: '5px',
        paddingBottom: '5px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        ...(noFlex ? {lineHeight: 1.5} : {flex: 1, lineHeight: 1}),
      }}
      title={title}
    >
      <Box
        sx={{
          lineHeight: 1,
          paddingTop: '5px',
          paddingBottom: '5px',
          paddingLeft: '8px',
          paddingRight: '8px',
          borderRadius: 5,
          // display: 'flex',
          // flexDirection: 'column',
          // justifyContent: 'center',
          // alignItems: 'center',
          background: isInColorScheme
            ? colorScheme[value?.toString() as any]
            : colorScheme.default,
          fontSize,
        }}
      >
        {children}
      </Box>
      {childrenPost}
    </Box>
  );
};

export const renderAgeDate: gridCellDef<SaltusAny> = (params) => {
  if (params.value) {
    const date = moment.utc(params.value?.valueOf() as any);
    return (
      <Box flex={1} textAlign="center" alignContent="center" justifyContent="center">
        {/* {date.format('DD/MM/YY')}  */}
        {Math.abs(date.diff(newUTCDate(), 'years'))}
      </Box>
    );
  }
  return <div />;
};
